import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Navbar = ({ pageSlug, menuId, menuItems: propMenuItems, darkHero = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [menuItems, setMenuItems] = useState(propMenuItems || []);
  // Use preloaded settings if available for instant logo display
  const [settings, setSettings] = useState(() => {
    if (typeof window !== 'undefined' && window.__PRELOADED_SETTINGS__) {
      return window.__PRELOADED_SETTINGS__;
    }
    return null;
  });
  const [ctaButton, setCtaButton] = useState({ text: 'Subscribe', link: '#newsletter' });
  // If settings were preloaded, logo is already ready
  const [logoLoaded, setLogoLoaded] = useState(() => {
    if (typeof window !== 'undefined' && window.__PRELOADED_SETTINGS__?.logo_url) {
      return true;
    }
    return false;
  });
  const logoRef = useRef(null);

  // Determine if we should use light theme (on dark hero, not scrolled)
  const useLightTheme = darkHero && !isScrolled;

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Skip settings fetch if already preloaded
        if (!settings) {
          const settingsRes = await axios.get(`${API}/settings`);
          setSettings(settingsRes.data);
          
          // Preload the logo image
          if (settingsRes.data?.logo_url) {
            const img = new Image();
            img.onload = () => setLogoLoaded(true);
            img.src = settingsRes.data.logo_url;
            // Also preload light logo if exists
            if (settingsRes.data.logo_url_light) {
              const lightImg = new Image();
              lightImg.src = settingsRes.data.logo_url_light;
            }
          } else {
            setLogoLoaded(true);
          }
        }

        // If menu items weren't passed as props, fetch them
        if (!propMenuItems) {
          let menuRes;
          if (menuId) {
            // Get menu by specific menu ID (used by homepage with assigned menu)
            menuRes = await axios.get(`${API}/menu?menu_id=${menuId}`);
            setMenuItems(menuRes.data || []);
            // Fetch menu details for CTA
            try {
              const menusRes = await axios.get(`${API}/menus`);
              const menu = menusRes.data.find(m => m.id === menuId);
              if (menu) {
                setCtaButton({
                  text: menu.cta_text || 'Subscribe',
                  link: menu.cta_link || '#newsletter'
                });
              }
            } catch (e) {
              console.error('Failed to fetch menu CTA:', e);
            }
          } else if (pageSlug) {
            // Get menu specific to this page
            menuRes = await axios.get(`${API}/menu/for-page/${pageSlug}`);
            setMenuItems(menuRes.data.items || []);
            setCtaButton({
              text: menuRes.data.cta_text || 'Subscribe',
              link: menuRes.data.cta_link || '#newsletter'
            });
          } else {
            // Get default menu (for homepage without menu assignment)
            menuRes = await axios.get(`${API}/menu`);
            setMenuItems(menuRes.data || []);
            // Also fetch default menu CTA info
            try {
              const menusRes = await axios.get(`${API}/menus`);
              const defaultMenu = menusRes.data.find(m => m.is_default);
              if (defaultMenu) {
                setCtaButton({
                  text: defaultMenu.cta_text || 'Subscribe',
                  link: defaultMenu.cta_link || '#newsletter'
                });
              }
            } catch (e) {
              console.error('Failed to fetch default menu CTA:', e);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch navbar data:', error);
      }
    };

    fetchData();
  }, [pageSlug, propMenuItems]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Transform menu items for display
  const navLinks = menuItems.filter(item => item.is_visible !== false).map(item => ({
    name: item.label,
    href: item.target || '#',
    openInNewTab: item.open_in_new_tab,
  }));

  // Choose the appropriate logo
  const logoUrl = useLightTheme && settings?.logo_url_light 
    ? settings.logo_url_light 
    : settings?.logo_url;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo - with preloading to prevent flash */}
          <a href="/" className="flex items-center" style={{ minHeight: `${settings?.logo_size || 40}px` }}>
            {logoUrl ? (
              <img 
                ref={logoRef}
                src={logoUrl} 
                alt={settings?.site_name || 'Pulse NYC'} 
                style={{ 
                  height: `${settings?.logo_size || 40}px`,
                  opacity: logoLoaded ? 1 : 0,
                  transition: 'opacity 0.15s ease-in'
                }}
                className="object-contain"
                data-testid="navbar-logo"
                fetchpriority="high"
                onLoad={() => setLogoLoaded(true)}
              />
            ) : (
              <span 
                className={`text-xl font-bold transition-colors duration-300 ${
                  useLightTheme ? 'text-white' : 'text-gray-900'
                }`} 
                data-testid="navbar-site-name"
              >
                {settings?.site_name || 'Pulse NYC'}
              </span>
            )}
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target={link.openInNewTab ? '_blank' : undefined}
                rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                className={`transition-colors duration-300 text-sm font-medium ${
                  useLightTheme 
                    ? 'text-white/90 hover:text-white' 
                    : 'text-gray-700 hover:text-black'
                }`}
                data-testid={`nav-link-${index}`}
              >
                {link.name}
              </a>
            ))}
            <a href={ctaButton.link}>
              <Button 
                className={`transition-all duration-300 ${
                  useLightTheme 
                    ? 'bg-white text-black hover:bg-gray-100' 
                    : 'bg-black hover:bg-gray-800 text-white'
                }`}
              >
                {ctaButton.text}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              useLightTheme 
                ? 'text-white hover:bg-white/10' 
                : 'text-gray-900 hover:bg-gray-100'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target={link.openInNewTab ? '_blank' : undefined}
                rel={link.openInNewTab ? 'noopener noreferrer' : undefined}
                className="block py-2 text-gray-700 hover:text-black transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a href={ctaButton.link} className="block">
              <Button className="w-full bg-black hover:bg-gray-800 text-white">
                {ctaButton.text}
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
